import { useState } from 'react';
import { collection, addDoc, serverTimestamp, doc, runTransaction } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../../firebase';
import { setSentryUser } from '../../utils/sentry';
import { trackBusinessMetrics } from '../../utils/sentryMetrics';
import { handleError } from '../../utils/errorNotification';
import { calculateTotalPrices } from '../../utils/applicationStatusCalculator';
import { useChannel } from '../../contexts/ChannelContext';
import { readStoredAttribution, toFirestoreInflow } from '../../utils/inflowAttribution';
import type { ChannelType } from '../admin/types/application';

const initialForm = {
  name: '',
  phone: '',
  devices: [] as { deviceType: string; deviceModel: string }[],
  pickupAddress: '',
  pickupAddressDetail: '',
  storeCode: '',
  bankName: '',
  bankAccount: '',
  bankOwner: '',
};

export const useApplicationForm = () => {
  const { channel } = useChannel();
  const [form, setForm] = useState(initialForm);
  const [agreed, setAgreed] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // 유효성 검사 함수
  const validateForm = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // 1. 반납기기 리스트 검사
    if (!form.devices || form.devices.length === 0) {
      errors.push('반납할 기기를 최소 1개 이상 선택해주세요.');
    } else {
      // 각 기기의 필수 정보 확인
      form.devices.forEach((device, index) => {
        if (!device.deviceType || device.deviceType.trim() === '') {
          errors.push(`${index + 1}번째 기기의 기기 종류를 선택해주세요.`);
        }
        if (!device.deviceModel || device.deviceModel.trim() === '') {
          errors.push(`${index + 1}번째 기기의 모델명을 선택해주세요.`);
        }
      });
    }

    // 2. 주소 입력 검사
    if (!form.pickupAddress || form.pickupAddress.trim() === '') {
      errors.push('수거 주소를 입력해주세요.');
    }
    if (!form.pickupAddressDetail || form.pickupAddressDetail.trim() === '') {
      errors.push('상세 주소를 입력해주세요.');
    }

    // 3. 은행명 + 계좌번호 검사
    if (!form.bankName || form.bankName.trim() === '') {
      errors.push('은행명을 선택해주세요.');
    }
    if (!form.bankAccount || form.bankAccount.trim() === '') {
      errors.push('계좌번호를 입력해주세요.');
    } else {
      // 계좌번호 형식 검사 (숫자와 하이픈만 허용)
      const accountPattern = /^[0-9-]+$/;
      if (!accountPattern.test(form.bankAccount.trim())) {
        errors.push('계좌번호는 숫자와 하이픈(-)만 입력 가능합니다.');
      }
      // 계좌번호 최소 길이 검사
      const cleanAccount = form.bankAccount.replace(/-/g, '');
      if (cleanAccount.length < 10) {
        errors.push('계좌번호는 최소 10자리 이상 입력해주세요.');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // 신청번호 생성
  const getNextApplicationNumber = async (): Promise<number> => {
    const counterRef = doc(db, 'counters', 'applications');
    return runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      const current = counterDoc.exists() ? counterDoc.data().current || 1000 : 1000;
      const next = current + 1;
      transaction.set(counterRef, { current: next });
      return next;
    });
  };

  // 신청서 제출
  const handleSubmit = async () => {
    // 유효성 검사 실행
    const validation = validateForm();
    
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      
      // Sentry에 유효성 검사 실패 로그 전송
      handleError(new Error('매입 신청 폼 유효성 검사 실패'), {
        business_type: 'application_submit',
        step: 'validation_failed',
        errors: validation.errors,
        form_data: {
          device_count: form.devices?.length || 0,
          has_pickup_address: !!form.pickupAddress,
          has_bank_info: !!(form.bankName && form.bankAccount),
          user_name: form.name,
        },
        timestamp: new Date().toISOString()
      }, 'warning');
      
      // 첫 번째 에러로 스크롤 (사용자 경험 개선)
      setTimeout(() => {
        const errorElement = document.querySelector('.validation-error');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      return;
    }
    
    // 유효성 검사 통과 시 에러 초기화
    setValidationErrors([]);
    setIsSubmitting(true);
    setBlocking(true);
    
    try {
      const nextNo = await getNextApplicationNumber();
      
      // 🆕 기기를 새로운 구조로 변환 (고유 ID 및 초기 상태 부여)
      const devicesWithIds = form.devices.map(device => ({
        id: uuidv4(), // 고유 식별자 생성
        deviceType: device.deviceType,
        deviceModel: device.deviceModel,
        status: '접수완료' as const, // 🔄 기기 초기 상태: 접수완료 (신청서 상태와 동기화)
        price_offer: null,
        final_price: null,
        grade: null,
        deduction_reason: null,
        reject_reason: null,
        // 🆕 신청 시 선택된 등급과 예상 금액 보존 (0원도 보존)
        userSelectedGrade: (device as any).userSelectedGrade || null,
        estimatedPrice: (device as any).estimatedPrice !== undefined ? (device as any).estimatedPrice : null,
        created_at: new Date(),
        updated_at: new Date()
      }));
      
      // 🔄 신규 신청은 수동으로 '접수완료' 상태로 설정
      const initialApplicationStatus = '접수완료';
      const { total_price_offer, total_final_price } = calculateTotalPrices(devicesWithIds);

      // 📈 첫 진입 시 수집해둔 마케팅 유입경로 (없으면 필드 자체 생략)
      const inflow = readStoredAttribution();

      const applicationRef = await addDoc(collection(db, 'applications'), {
        application_number: nextNo,
        user_name: form.name,
        phone_number: form.phone,
        devices: devicesWithIds,
        pickup_address: form.pickupAddress,
        pickup_address_detail: form.pickupAddressDetail,
        agreed_to_privacy: agreed,
        auth_verified: true,
        status: initialApplicationStatus,
        
        total_price_offer,
        total_final_price,
        
        price_offer: total_price_offer,
        final_price: total_final_price,
        
        // 채널 및 유입 경로 정보
        channel: channel as ChannelType,
        source_type: 'direct',
        store_code: channel === 'b2c' ? '' : form.storeCode,

        // 마케팅 유입경로 (utm/referrer 기반, 미수집 시 필드 생략)
        ...(inflow ? { inflow: toFirestoreInflow(inflow) } : {}),

        bank_name: form.bankName,
        bank_account: form.bankAccount,
        account_holder: form.name,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
      
      // 알림톡 전송
      const alertResponse = await fetch('https://us-central1-refurb-buy.cloudfunctions.net/api/api/alerts/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: applicationRef.id,
          user_name: form.name,
          phone_number: form.phone,
          device_name: form.devices?.map(d => d.deviceModel).join(', ') || '기기정보없음',
          apply_date: new Date().toISOString().split('T')[0],
          event: '신청완료',
        })
      });
      
      if (!alertResponse.ok) {
        // 알림톡 전송 실패는 warning 레벨로 처리 (신청은 성공했으므로)
        handleError(new Error('알림톡 전송 실패'), {
          business_type: 'biztalk_send',
          application_id: applicationRef.id,
          event_type: '신청완료',
          response_status: alertResponse.status
        }, 'warning');
      } else {
        // 알림톡 전송 성공 메트릭
        trackBusinessMetrics.alertSendSuccess('신청완료');
      }
      
      // 신청 성공 메트릭 추적
      trackBusinessMetrics.applicationSubmitSuccess(form.devices.length, form.name);
      
      // 유효성 검사 통과 및 신청 성공 메트릭
      import('../../utils/sentry').then(({ trackBusinessSuccess }) => {
        trackBusinessSuccess('application_submit', '매입 신청 완료', {
          device_count: form.devices.length,
          has_store_code: !!form.storeCode,
          bank_name: form.bankName,
          channel,
          validation_passed: true
        });
      });
      
      setSubmitSuccess(true);
      setShowSuccessDialog(true);
      
    } catch (error) {
      console.error('❌ 신청서 제출 실패:', error);
      
      // 신청 실패는 critical 에러로 처리
      handleError(error as Error, {
        business_type: 'application_submit',
        step: 'application_submit',
        form_data: {
          device_count: form.devices.length,
          has_pickup_address: !!form.pickupAddress,
          bank_name: form.bankName,
          user_name: form.name,
        },
        timestamp: new Date().toISOString()
      }, 'critical');
      
      alert('신청서 제출에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setIsSubmitting(false);
      setBlocking(false);
    }
  };

  return {
    // 상태
    form,
    setForm,
    agreed,
    setAgreed,
    isAuthenticated,
    setIsAuthenticated,
    isSubmitting,
    submitSuccess,
    blocking,
    setBlocking,
    showSuccessDialog,
    setShowSuccessDialog,
    validationErrors,
    
    // 함수
    handleSubmit,
  };
}; 